import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { api } from "../lib/api";
import BikeCard from "../components/BikeCard";
import Testimonials from "../components/Testimonials";
import { ArrowRight, ArrowLeft, MapPin, Wrench, Sparkles, CheckCircle2 } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_ls39-bikes/artifacts/4b83pim3_ChatGPT%20Image%2013%20may%202026%2C%2011_31_45.png";
const ABOUT_IMG = "https://images.unsplash.com/photo-1674747605562-05b5b86c6a21";
const SERVICE_IMG = "https://customer-assets.emergentagent.com/job_ls39-bikes/artifacts/m249fgjj_ls39%20taller.webp";
const LUIS_IMG = "/local_img/luis-salom-hor.webp";
const FATHERS_IMG = "/local_img/maria_y_jose_rec.jpg";

export default function Home() {
    const { lang, t } = useLang();
    const [bikes, setBikes] = useState([]);

    useEffect(() => {
        api.get("/bikes", { params: { featured: true } }).then((r) => setBikes(r.data || []));
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative isolate min-h-[92vh] overflow-hidden">
                <div className="absolute inset-0 -z-10">
                    <img src={HERO_IMG} alt="LS39 Mallorca dealership" className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-white/40 to-[--ls-bg]" />
                    <div className="absolute inset-0 bg-gradient-to-l from-white/95 via-white/60 to-transparent" />
                </div>

                <div className="mx-auto flex min-h-[92vh] max-w-7xl flex-col justify-start px-6 pt-24 pb-24 lg:justify-end lg:px-10 lg:pt-0 lg:pb-32">
                    <div className="lg:ml-auto lg:max-w-2xl lg:text-right">
                        <p className="text-overline mb-6 fade-up">{t.hero.overline}</p>
                        <h1
                            className="font-display text-5xl font-bold leading-[0.95] tracking-tighter text-neutral-900 sm:text-7xl lg:text-[7rem] fade-up"
                            style={{ animationDelay: "0.1s" }}
                        >
                            {t.hero.title_1}
                            <br />
                            <span style={{ color: "var(--ls-terracotta)" }}>{t.hero.title_2}</span>
                        </h1>
                        <p
                            className="mt-8 max-w-xl text-base text-neutral-700 leading-relaxed fade-up lg:ml-auto"
                            style={{ animationDelay: "0.2s" }}
                        >
                            {t.hero.subtitle}
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4 fade-up lg:justify-end" style={{ animationDelay: "0.3s" }}>
                            <Link to="/motos" className="btn-primary" data-testid="hero-cta-new">
                                {t.hero.cta_primary}
                                <ArrowRight size={16} />
                            </Link>
                            <Link to="/contacto" className="btn-outline" data-testid="hero-cta-test">
                                {t.hero.cta_secondary}
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* INFO CAROUSEL */}
            <InfoCarousel t={t} />

            {/* FEATURED BIKES */}
            <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="featured-section">
                <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
                    <div className="max-w-xl">
                        <p className="text-overline">{t.sections.featured}</p>
                        <h2 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
                            {t.sections.new_title}
                        </h2>
                        <p className="mt-4 text-base text-neutral-500">{t.sections.featured_desc}</p>
                    </div>
                    <Link to="/motos" className="text-sm uppercase tracking-[0.25em] text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-2">
                        {t.bike.view} <ArrowRight size={14} />
                    </Link>
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {bikes.slice(0, 6).map((b, i) => (
                        <BikeCard key={b.id} bike={b} index={i} />
                    ))}
                </div>
            </section>

            {/* SERVICE BLOCK */}
            <section className="relative overflow-hidden border-y border-black/10" data-testid="service-section">
                <div className="absolute inset-0 -z-10">
                    <img src={SERVICE_IMG} alt="" className="h-full w-full object-cover opacity-25" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[--ls-bg] via-[--ls-bg]/80 to-transparent" />
                </div>
                <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
                    <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
                        <div className="lg:col-span-6">
                            <p className="text-overline">{t.sections.service_title}</p>
                            <h2 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
                                {t.sections.service_title}.
                            </h2>
                            <p className="mt-6 max-w-lg text-base text-neutral-600">{t.sections.service_desc}</p>
                            <div className="mt-10">
                                <Link to="/servicio" className="btn-accent" data-testid="service-cta">
                                    {t.forms.book}
                                    <ArrowRight size={16} />
                                </Link>
                            </div>
                        </div>

                        <div className="lg:col-span-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                            {[
                                { icon: Wrench,       label_es: "Mantenimiento oficial", label_en: "Official maintenance", serviceType: "Maintenance" },
                                { icon: Sparkles,     label_es: "Preparación de pista",  label_en: "Track prep",           serviceType: "Track Prep" },
                                { icon: MapPin,       label_es: "ITV asistida",           label_en: "ITV assistance",       serviceType: "ITV" },
                                { icon: CheckCircle2, label_es: "Trabajos custom",        label_en: "Custom builds",        serviceType: "Custom Work" },
                            ].map((item, i) => (
                                <Link key={i} to="/servicio" state={{ serviceType: item.serviceType }} className="glass border border-black/10 p-6 hover:border-black/25 transition-colors block">
                                    <item.icon size={22} style={{ color: "var(--ls-terracotta)" }} />
                                    <p className="mt-6 font-display text-xl font-semibold">
                                        {lang === "es" ? item.label_es : item.label_en}
                                    </p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ABOUT MALLORCA */}
            <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="about-section">
                <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <p className="text-overline">Mallorca</p>
                        <h2 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
                            {t.sections.about_title}
                        </h2>
                        <p className="mt-6 text-base text-neutral-600">{t.sections.about_desc}</p>
                        <div className="mt-10 grid grid-cols-2 gap-6">
                            <div>
                                <p className="font-mono text-5xl font-bold" style={{ color: "var(--ls-terracotta)" }}>
                                    20+
                                </p>
                                <p className="mt-2 text-xs uppercase tracking-[0.2em] text-neutral-500">
                                    {t.sections.years_on_island}
                                </p>
                            </div>
                            <div>
                                <p className="font-mono text-5xl font-bold" style={{ color: "var(--ls-terracotta)" }}>
                                    1.4k
                                </p>
                                <p className="mt-2 text-xs uppercase tracking-[0.2em] text-neutral-500">
                                    {t.sections.bikes_sold}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="relative aspect-[4/3] overflow-hidden border border-black/10">
                            <img src={ABOUT_IMG} alt="Dealership" className="h-full w-full object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <Testimonials limit={6} />
        </div>
    );
}

function InfoCarousel({ t }) {
    const { lang } = useLang();
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
    const [selectedIndex, setSelectedIndex] = useState(0);
    const intervalRef = useRef(null);

    const startAutoplay = useCallback(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => emblaApi?.scrollNext(), 4500);
    }, [emblaApi]);

    const scrollPrev = useCallback(() => { emblaApi?.scrollPrev(); startAutoplay(); }, [emblaApi, startAutoplay]);
    const scrollNext = useCallback(() => { emblaApi?.scrollNext(); startAutoplay(); }, [emblaApi, startAutoplay]);

    useEffect(() => {
        if (!emblaApi) return;
        const onSelect = () => {
            setSelectedIndex(emblaApi.selectedScrollSnap());
            startAutoplay();
        };
        emblaApi.on("select", onSelect);
        return () => emblaApi.off("select", onSelect);
    }, [emblaApi, startAutoplay]);

    useEffect(() => {
        if (!emblaApi) return;
        startAutoplay();
        return () => clearInterval(intervalRef.current);
    }, [emblaApi, startAutoplay]);

    const slides = [
        {
            img: LUIS_IMG,
            title: t.carousel.ls_title,
            subtitle: t.carousel.ls_subtitle,
            cta: t.carousel.ls_cta,
            to: "/historia",
        },
        {
            img: FATHERS_IMG,
            title: t.carousel.dealer_title,
            subtitle: t.carousel.dealer_subtitle,
            cta: t.carousel.dealer_cta,
            to: "/nosotros",
        },
        {
            img: SERVICE_IMG,
            title: t.carousel.team_title,
            subtitle: t.carousel.team_subtitle,
            cta: t.carousel.team_cta,
            to: "/equipo",
        },
    ];

    return (
        <section className="relative border-b border-black/10" data-testid="info-carousel">
            <div className="overflow-hidden" ref={emblaRef}>
                <div className="flex touch-pan-y">
                    {slides.map((slide, i) => (
                        <div
                            key={i}
                            className="relative flex-none w-full min-h-[55vh] overflow-hidden"
                            data-testid={`carousel-slide-${i}`}
                        >
                            <img
                                src={slide.img}
                                alt=""
                                aria-hidden
                                className="absolute inset-0 h-full w-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/20" />
                            <div className="relative mx-auto max-w-7xl px-6 py-16 lg:px-10 flex items-center min-h-[55vh]">
                                <div className="max-w-2xl">
                                    <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl leading-tight">
                                        {slide.title}
                                    </h2>
                                    <p className="mt-4 text-base text-white/80 leading-relaxed max-w-lg">
                                        {slide.subtitle}
                                    </p>
                                    <Link
                                        to={slide.to}
                                        className="mt-8 inline-flex items-center gap-2 btn-primary"
                                    >
                                        {slide.cta} <ArrowRight size={16} />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Prev / Next */}
            <button
                onClick={scrollPrev}
                aria-label={lang === "es" ? "anterior" : "previous"}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/75 text-white p-3 transition-colors hidden sm:flex"
            >
                <ArrowLeft size={18} />
            </button>
            <button
                onClick={scrollNext}
                aria-label={lang === "es" ? "siguiente" : "next"}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/75 text-white p-3 transition-colors hidden sm:flex"
            >
                <ArrowRight size={18} />
            </button>

            {/* Dots */}
            <style>{`@keyframes carousel-progress { from { width: 0% } to { width: 100% } }`}</style>
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2.5 z-10">
                {slides.map((_, i) => (
                    <button
                        key={i}
                        onClick={() => { emblaApi?.scrollTo(i); startAutoplay(); }}
                        aria-label={`slide ${i + 1}`}
                        className={`relative overflow-hidden h-1.5 rounded-full transition-all duration-300 ${
                            i === selectedIndex ? "w-8 bg-white/30" : "w-1.5 bg-white/40"
                        }`}
                    >
                        {i === selectedIndex && (
                            <span
                                key={selectedIndex}
                                className="absolute inset-y-0 left-0 bg-white rounded-full"
                                style={{ animation: "carousel-progress 4500ms linear forwards", width: "0%" }}
                            />
                        )}
                    </button>
                ))}
            </div>
        </section>
    );
}
